/**
 * Hooks for fetching single reward instance and related data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rewardsApi } from '@api/endpoints/rewards'

/**
 * Fetch a single reward instance
 */
export function useRewardInstance(instanceId: string | undefined) {
  return useQuery({
    queryKey: ['reward-instance', instanceId],
    queryFn: () => rewardsApi.getRewardInstance(instanceId!),
    enabled: !!instanceId,
  })
}

/**
 * Fetch QR code data for a reward
 */
export function useRewardQR(instanceId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: ['reward-qr', instanceId],
    queryFn: () => rewardsApi.getRewardQR(instanceId!),
    enabled: !!instanceId && enabled,
  })
}

/**
 * Check if a reward can be transferred
 */
export function useCanTransferReward(instanceId: string | undefined) {
  return useQuery({
    queryKey: ['can-transfer-reward', instanceId],
    queryFn: () => rewardsApi.canTransferReward(instanceId!),
    enabled: !!instanceId,
  })
}

/**
 * Transfer a reward to another user
 */
export function useTransferReward() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ instanceId, toUserId }: { instanceId: string; toUserId: string }) =>
      rewardsApi.transferReward(instanceId, toUserId),
    onSuccess: (data, variables) => {
      // Invalidate the instance query
      queryClient.invalidateQueries({ queryKey: ['reward-instance', variables.instanceId] })
      // Invalidate the list
      queryClient.invalidateQueries({ queryKey: ['my-rewards'] })
      // Invalidate can-transfer
      queryClient.invalidateQueries({ queryKey: ['can-transfer-reward', variables.instanceId] })
    },
  })
}
